import React from "react";
import { useNavigate } from "react-router-dom";
import kleidologo from "../../../../assets/images/Kleido.png";
import StoreLinks from "../../../common/StoreLinks";
import { createPortal } from "react-dom";

/* ------------------------------------------------------------------
   GLOBAL FEATURE FLAGS (build-time, from .env in the project root)
   REACT_APP_JOIN_ENABLED=true enables "Join Kleido AI", which opens
   the beta app (src-beta/) under /beta.
   Anything that is not exactly "true" resolves to false, so a
   missing / misspelled value can never accidentally open sign-ups.
   Uses the same check as src/index.js, so the button is only enabled
   when the beta app is actually mounted at /beta.
   Restart `npm start` after changing .env.
------------------------------------------------------------------- */
const FLAGS = {
  JOIN_ENABLED: process.env.REACT_APP_JOIN_ENABLED === "true",
};

/** Where "Join Kleido AI" goes: the beta app (src-beta/). */
const BETA_ENTRY = "/beta";

/* Which app serves the shared URLs (/signin, /signup, /user-feed/...).
   src/index.jsx reads this on every full page load:
     "demo" → src/ (Experience Kleido AI: local-storage sign-in, no API calls)
     "beta" → src-beta/ (Join Kleido AI: API sign-in)
   Keep the key and values in sync with src/index.jsx. */
const APP_MODE_KEY = "kleidoAppMode";
const setAppMode = (mode) => {
  try {
    localStorage.setItem(APP_MODE_KEY, mode);
  } catch (err) {
    console.error(err);
  }
};

const NOT_LIVE_MESSAGE =
  "Kleido AI is not live yet. Sign-ups are closed — please wait for the app to go live.";

const features = [
  {
    name: "Content Ownership",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
  },
  {
    name: "Privacy First",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
  },
  {
    name: "Quality First",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
  },
  {
    name: "Fair Monetization",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
  },
  {
    name: "Complete Control",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07M8.46 8.46a5 5 0 0 0 0 7.07"/>
      </svg>
    ),
  },
  {
    name: "AI Powered Features",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
        <path d="M2 17l10 5 10-5"/>
        <path d="M2 12l10 5 10-5"/>
      </svg>
    ),
  },
];

function StarField() {
  const stars = React.useMemo(() => {
    return Array.from({ length: 55 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 1.6 + 0.4,
      delay: Math.random() * 4,
      duration: Math.random() * 3 + 2,
    }));
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {stars.map((s) => (
        <div
          key={s.id}
          style={{
            position: "absolute",
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: "50%",
            background: "white",
            opacity: 0,
            animation: `starPulse ${s.duration}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function FeatureItem({ icon, name, index }) {
  return (
    <div
      className="feature-item"
      style={{ animationDelay: `${0.6 + index * 0.1}s` }}
    >
      <span className="feature-icon">{icon}</span>
      <span className="feature-name">{name}</span>
    </div>
  );
}

const PEEK_PRODUCTS = [
  {
    key: "connect",
    name: "Kleido Connect",
    tagline: "Find your people",
    body: "Connect through the passions, interests and communities that matter to you.",
  },
  {
    key: "sky",
    name: "Kleido Sky",
    tagline: "KNOW BEFORE IT MATTERS",
    body: "Understand changing weather and potential hazards before they affect where you live, travel or gather.",
  },
  {
    key: "insight",
    name: "Kleido Insight",
    tagline: "TRUST REAL EXPERIENCES",
    body: "Discover what people actually experienced, so you can make better decisions with confidence.",
  },
  {
    key: "gather",
    name: "Kleido Gather",
    tagline: "CELEBRATE WHAT UNITES US",
    body: "Discover the festivals, traditions and stories that connect generations and bring communities together.",
  },
];

function ValuePeek() {
  const [active, setActive] = React.useState(null);
  const wrapRef = React.useRef(null);
  const [anchor, setAnchor] = React.useState(null);

  const openAt = (product) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setAnchor({ top: r.bottom + 14, left: r.left + r.width / 2 });
    setActive(product);
  };

  React.useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <span className="peek-wrap" ref={wrapRef} style={{marginLeft: 80}}>
      <span className="peek-grid">
        {PEEK_PRODUCTS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`peek-tile${active?.key === p.key ? " is-active" : ""}`}
            aria-label={p.name}
            aria-expanded={active?.key === p.key}
            onMouseEnter={() => openAt(p)}
            onFocus={() => openAt(p)}
            onClick={() => (active?.key === p.key ? setActive(null) : openAt(p))}
          />
        ))}
      </span>

      {active && anchor && createPortal(
        <div
          className="peek-pop"
          role="dialog"
          aria-label={active.name}
          style={{ top: anchor.top, left: anchor.left }}
          onMouseLeave={() => setActive(null)}
        >
          <button
            type="button"
            className="peek-close"
            aria-label="Close"
            onClick={() => setActive(null)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <span className="peek-pop-tag">{active.tagline}</span>
          <h3 className="peek-pop-title">{active.name}</h3>
          <p className="peek-pop-body">{active.body}</p>
        </div>,
        document.body
      )}
    </span>
  );
}

export function Landing() {
  const navigate = useNavigate();

  // Error shown when a gated CTA is clicked while its flag is off.
  const [flagError, setFlagError] = React.useState("");

  // Auto-dismiss the message so it never sticks around.
  React.useEffect(() => {
    if (!flagError) return undefined;
    const t = setTimeout(() => setFlagError(""), 5000);
    return () => clearTimeout(t);
  }, [flagError]);

  const joinDisabled = !FLAGS.JOIN_ENABLED;

  const handleJoin = (e) => {
    e.preventDefault();
    if (joinDisabled) {
      setFlagError(NOT_LIVE_MESSAGE);
      return;
    }
    setFlagError("");
    setAppMode("beta");
    // Full page load (not navigate()): /beta is a separate app (src-beta/),
    // mounted by src/index.js. navigate() would stay inside the demo app.
    window.location.assign(BETA_ENTRY);
  };

  const handleExperience = () => {
    try {
      const raw = localStorage.getItem("users");
      const users = raw ? JSON.parse(raw) : [];
      if (!users.find((u) => u.userId === "sample")) {
        users.push({
          identifier: "sample",
          userId: "sample",
          password: "password",
        });
      }
      localStorage.setItem("users", JSON.stringify(users));
      setAppMode("demo");
      // Stays inside this demo app (src/): its /signin is the local-storage
      // sign-in, which then goes on to /user-feed/:userId here too.
      navigate("/signin-ex");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <style>{`
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        :root {
          --bg-deep: #070710;
          --bg-left: #0b0b18;
          --bg-right: #090912;
          --purple-core: #7c3aed;
          --purple-light: #a78bfa;
          --purple-glow: rgba(124, 58, 237, 0.25);
          --silver: #c8c8d8;
          --silver-dim: #8888a0;
          --white: #ffffff;
          --black: #111111;
          --danger: #b91c1c;
          --danger-bg: rgba(185, 28, 28, 0.08);
          --border-subtle: rgba(168, 139, 250, 0.12);
          --border-mid: rgba(168, 139, 250, 0.22);
          --font-display: 'Georgia', 'Times New Roman', serif;
          --font-body: 'Trebuchet MS', 'Lucida Sans Unicode', system-ui, sans-serif;
        }

        html, body, #root {
          height: 100%;
          width: 100%;
        }

        body {
          background: var(--bg-deep);
          color: var(--white);
          font-family: var(--font-body);
          overflow-x: hidden;
        }

        @keyframes starPulse {
          0%, 100% { opacity: 0; transform: scale(0.8); }
          50% { opacity: 0.7; transform: scale(1.2); }
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeSlideRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fadeSlideLeft {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes glowPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.08); }
        }

        @keyframes gridScroll {
          from { transform: translateY(0); }
          to { transform: translateY(40px); }
        }

        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        @keyframes borderGlow {
          0%, 100% { border-color: rgba(255,255,255,0.9); box-shadow: 0 0 0 rgba(0,0,0,0); }
          50% { border-color: rgba(0,0,0,0.6); box-shadow: 0 0 18px rgba(255,255,255,0.55); }
        }

        .landing-root {
          display: flex;
          flex-direction: row;
          min-height: 100vh;
          width: 100%;
          position: relative;
          overflow: hidden;
        }

        /* ---- DIVIDER ---- */
        .landing-divider {
          width: 1px;
          background: linear-gradient(to bottom,
            transparent 0%,
            white 40%,
            black 60%,
            transparent 100%
          );
          flex-shrink: 0;
          position: relative;
          z-index: 2;
        }

        .landing-divider-right {
          width: 1px;
          background: linear-gradient(to bottom,
            transparent 0%,
            black 40%,
            white 60%,
            transparent 100%
          );
          flex-shrink: 0;
          position: relative;
          z-index: 2;
        }

        .landing-divider-right::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: black;
          box-shadow: 0 0 14px 4px var(--purple-glow);
        }

        .landing-divider::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: white;
          box-shadow: 0 0 14px 4px var(--purple-glow);
        }

        /* ---- LEFT PANEL ---- */
        .panel-left {
          flex: 0 0 42%;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 56px 52px 56px 60px;
          position: relative;
          background: black;
          overflow: hidden;
        }

        .panel-left::before {
          content: '';
          position: absolute;
          top: -120px;
          left: -80px;
          width: 420px;
          height: 420px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(109,40,217,0.18) 0%, transparent 70%);
          animation: glowPulse 6s ease-in-out infinite;
          pointer-events: none;
        }

        .panel-left::after {
          content: '';
          position: absolute;
          bottom: -80px;
          right: -40px;
          width: 280px;
          height: 280px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%);
          pointer-events: none;
        }

        /* Grid texture overlay */
        .grid-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0.04;
          background-image:
            linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px);
          background-size: 44px 44px;
          animation: gridScroll 12s linear infinite;
        }

        .panel-left-content {
          position: relative;
          z-index: 2;
        }

        /* LOGO */
        .logo-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 52px;
          opacity: 0;
          animation: fadeSlideRight 0.7s ease forwards 0.1s;
          width: 250px;
          height: 250px;
        }

        .logo-img {
          width: 300px;
          height: 300px;
          object-fit: contain;
          filter: brightness(1.1);
        }

        .logo-wordmark {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .logo-text {
          font-family: var(--font-display);
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.06em;
          color: var(--white);
        }

        .logo-badge {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: white;
          opacity: 0.85;
        }

        /* HEADING */
        .main-heading {
          font-family: var(--font-display);
          font-size: clamp(2rem, 3.5vw, 3rem);
          font-weight: 700;
          line-height: 1.15;
          letter-spacing: -0.01em;
          color: white;
          margin-bottom: 16px;
          opacity: 0;
          animation: fadeSlideRight 0.7s ease forwards 0.25s;
        }

        .main-heading em {
          font-style: italic;
          background: linear-gradient(135deg, var(--white) 0%, var(--silver-dim) 60%, black 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* SUBHEADING */
        .sub-heading {
          font-size: clamp(0.85rem, 1.1vw, 1rem);
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--silver-dim);
          margin-bottom: 44px;
          opacity: 0;
          animation: fadeSlideRight 0.7s ease forwards 0.38s;
        }

        /* FEATURE LIST */
        .features-label {
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--silver-dim);
          margin-bottom: 16px;
          opacity: 0;
          animation: fadeSlideRight 0.7s ease forwards 0.5s;
        }

        .features-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 20px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid var(--border-subtle);
          background: rgba(255,255,255,0.025);
          backdrop-filter: blur(4px);
          cursor: default;
          transition: border-color 0.25s ease, background 0.25s ease, transform 0.2s ease;
          opacity: 0;
          animation: fadeSlideRight 0.55s ease forwards;
        }

        .feature-item:hover {
          border-color: var(--border-mid);
          background: rgba(124,58,237,0.08);
          transform: translateX(3px);
        }

        .feature-icon {
          color: var(--silver-dim);
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }

        .feature-name {
          font-size: 12.5px;
          font-weight: 500;
          color: var(--white);
          letter-spacing: 0.02em;
          white-space: nowrap;
        }

        /* ---- RIGHT PANEL ---- */
        .panel-right {
          flex: 0 0 58%;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          padding: 56px 60px 56px 64px;
          position: relative;
          background: white;
          overflow: hidden;
        }

        .panel-right::before {
          content: '';
          position: absolute;
          top: 50%;
          right: -100px;
          transform: translateY(-50%);
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(109,40,217,0.14) 0%, transparent 65%);
          animation: glowPulse 8s ease-in-out 2s infinite;
          pointer-events: none;
        }

        .panel-right::after {
          content: '';
          position: absolute;
          bottom: 60px;
          left: 60px;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(192,132,252,0.08) 0%, transparent 70%);
          pointer-events: none;
        }

        .panel-right-content {
          position: relative;
          z-index: 2;
          max-width: 520px;
          width: 100%;
        }

        /* EYEBROW */
        .eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 5px 14px;
          border-radius: 999px;
          border: 1px solid white;
          background: black;
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          margin-bottom: 32px;
          opacity: 0;
          animation: fadeSlideLeft 0.7s ease forwards 0.2s;
        }

        .eyebrow-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--white);
          animation: glowPulse 2s ease-in-out infinite;
        }

        /* RIGHT HEADLINE */
        .right-headline {
          font-family: var(--font-display);
          font-size: clamp(1.8rem, 3.2vw, 2.8rem);
          font-weight: 700;
          line-height: 1.18;
          letter-spacing: -0.02em;
          color: var(--black);
          margin-bottom: 48px;
          opacity: 0;
          animation: fadeSlideLeft 0.75s ease forwards 0.35s;
        }

        .right-headline .accent-line {
          display: block;
          background: linear-gradient(90deg, #000000, #d4d4d4, var(--silver));
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite 1.2s;
        }

        /* CTA CARDS */
        .cta-stack {
          display: flex;
          flex-direction: column;
          gap: 18px;
          width: 100%;
          opacity: 0;
          animation: fadeSlideLeft 0.8s ease forwards 0.55s;
        }

        .cta-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 28px;
          border-radius: 16px;
          border: 1px solid var(--border-subtle);
          text-decoration: none;
          cursor: pointer;
          transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, opacity 0.25s ease;
          position: relative;
          overflow: hidden;
          width: 100%;
          text-align: left;
          font: inherit;
        }

        .cta-card::before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .cta-card:hover {
          transform: translateY(-3px);
        }

        .cta-card:hover::before {
          opacity: 1;
        }

        /* Primary CTA */
        .cta-primary {
          background: linear-gradient(135deg, rgba(109,40,217,0.35) 0%, rgba(76,29,149,0.25) 100%);
          border-color: rgba(89, 77, 122, 0.3);
        }

        .cta-primary::before {
          background: linear-gradient(135deg, rgba(109,40,217,0.5) 0%, rgba(76,29,149,0.4) 100%);
        }

        .cta-primary:hover {
          border-color: rgba(168,139,250,0.55);
          box-shadow: 0 8px 40px rgba(109,40,217,0.25), 0 0 0 1px rgba(168,139,250,0.1);
        }

        /* Secondary CTA */
        .cta-secondary {
          background: rgba(255,255,255,0.03);
          border-color: var(--border-subtle);
          animation: borderGlow 4s ease-in-out 2s infinite;
        }

        .cta-secondary:hover {
          background: rgba(255,255,255,0.065);
          border-color: rgba(168,139,250,0.4);
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          animation: none;
        }

        /* ---- DISABLED (flag off) CTA ---- */
        .cta-card.is-disabled {
          opacity: 0.55;
          filter: grayscale(0.5);
          cursor: not-allowed;
          animation: none;
        }

        .cta-card.is-disabled:hover {
          transform: none;
          box-shadow: none;
        }

        .cta-lock {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #555;
          border: 1px solid rgba(0,0,0,0.18);
          border-radius: 999px;
          padding: 2px 8px;
          margin-left: 10px;
          white-space: nowrap;
        }

        .flag-error {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 14px;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid rgba(185, 28, 28, 0.35);
          background: var(--danger-bg);
          color: var(--danger);
          font-size: 13px;
          letter-spacing: 0.02em;
          animation: fadeSlideUp 0.3s ease;
        }

        .cta-text-block {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .cta-title {
          font-family: var(--font-display);
          font-size: 17px;
          font-weight: 600;
          color: var(--white);
          letter-spacing: 0.01em;
        }

        .cta-subtitle {
          font-size: 12px;
          color: var(--black);
          letter-spacing: 0.04em;
        }

        .cta-arrow {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(0,0,0,0.6);
          background: rgba(0,0,0,0.06);
          color: var(--black);
          flex-shrink: 0;
          transition: transform 0.25s ease, background 0.25s ease;
        }

        /* Footer note */
        .right-footer-note {
          margin-top: 36px;
          font-size: 11px;
          color: rgba(136,136,160,0.6);
          letter-spacing: 0.06em;
          opacity: 0;
          animation: fadeSlideLeft 0.7s ease forwards 0.8s;
        }

        /* ---- RESPONSIVE: TABLET ---- */
        @media (max-width: 900px) {
          .landing-root {
            flex-direction: column;
          }

          .panel-left {
            flex: none;
            width: 100%;
            min-height: auto;
            padding: 48px 32px 40px;
            border-bottom: 1px solid var(--border-subtle);
          }

          .panel-left::before {
            width: 300px;
            height: 300px;
          }

          .landing-divider {
            display: none;
          }

          .panel-right {
            flex: none;
            width: 100%;
            min-height: auto;
            padding: 40px 32px 56px;
            align-items: flex-start;
          }

          .panel-right-content {
            max-width: 100%;
          }

          .features-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        /* ---- RESPONSIVE: MOBILE ---- */
        @media (max-width: 540px) {
          .panel-left {
            padding: 36px 20px 32px;
          }

          .panel-right {
            padding: 32px 20px 48px;
          }

          .features-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .logo-text {
            font-size: 18px;
          }

          .cta-card {
            padding: 18px 20px;
          }

          .cta-title {
            font-size: 15px;
          }
        }

        @keyframes veilIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @keyframes stageIn {
          from { opacity: 0; transform: translate(-50%, -46%) scale(0.94); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }

                @keyframes popIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px) scale(0.96); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }

        .peek-wrap {
          position: relative;
          display: inline-flex;
          vertical-align: middle;
          margin-left: 16px;
        }

        .peek-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 4px;
          padding: 5px;
          border-radius: 12px;
          border: 1px solid rgba(0, 0, 0, 0.14);
          background: rgba(0, 0, 0, 0.03);
        }

        .peek-tile {
          width: 15px;
          height: 15px;
          padding: 0;
          border-radius: 4px;
          border: 1.6px solid rgba(0, 0, 0, 0.55);
          background: transparent;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }

        .peek-tile:hover,
        .peek-tile:focus-visible,
        .peek-tile.is-active {
          background: black;
          border-color: var(--black-core);
          transform: scale(1.14);
          outline: none;
          box-shadow: 0 0 12px rgba(238, 237, 241, 0.55);
        }

        .peek-pop {
          position: fixed;
          transform: translateX(-50%);
          z-index: 2147483647;
          width: 300px;
          padding: 18px 20px 20px;
          border-radius: 18px;
          background: #06060d;
          border: 1px solid rgba(255, 255, 255, 0.16);
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65),
                      inset 0 1px 0 rgba(255, 255, 255, 0.08);
          animation: popIn 0.24s cubic-bezier(0.2, 0.9, 0.3, 1) both;
        }

        .peek-pop::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 18px;
          opacity: 0.06;
          background-image:
            linear-gradient(to right, rgba(255,255,255,0.9) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.9) 1px, transparent 1px);
          background-size: 28px 28px;
          pointer-events: none;
        }

        .peek-pop-tag {
          display: block;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: white;
        }

        .peek-pop-title {
          font-family: var(--font-display);
          font-size: 21px;
          font-weight: 700;
          color: #fff;
          margin: 7px 0 9px;
        }

        .peek-pop-body {
          font-family: var(--font-body);
          font-size: 13px;
          line-height: 1.55;
          color: rgba(255, 255, 255, 0.68);
        }

        .peek-close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 26px;
          height: 26px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(255, 255, 255, 0.16);
          background: rgba(255, 255, 255, 0.05);
          color: var(--silver);
          cursor: pointer;
          z-index: 3;
          transition: background 0.2s ease, color 0.2s ease;
        }

        .peek-close:hover { background: rgba(255,255,255,0.12); color: #fff; }
      `}</style>

      <main className="landing-root" role="main">
        <StarField />

        {/* ===== LEFT PANEL ===== */}
        <section className="panel-left" aria-label="Kleido AI brand values">
          <div className="grid-overlay" aria-hidden="true" />

          <div className="panel-left-content">
            {/* LOGO */}
            <div className="logo-wrap">
              {kleidologo ? (
                <img
                  src={kleidologo}
                  alt="Kleido AI logo"
                  className="logo-img"
                />
              ) : (
                <svg width="200" height="200" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                  <rect width="200" height="200" rx="8" fill="url(#logoGrad)"/>
                  <path d="M10 8L18 16L10 24" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="22" cy="16" r="3" fill="white" fillOpacity="0.85"/>
                  <defs>
                    <linearGradient id="logoGrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#7c3aed"/>
                      <stop offset="100%" stopColor="#4c1d95"/>
                    </linearGradient>
                  </defs>
                </svg>
              )}
            </div>

            {/* MAIN HEADING */}
            <h1 className="main-heading">
              Every content<br />
              <em>has a value</em>
            </h1>

            {/* SUBHEADING */}
            <p className="sub-heading">Built on principles, not promises</p>

            {/* FEATURES */}
            <p className="features-label">Core Principles</p>
            <div className="features-grid" role="list" >
              {features.map((f, i) => (
                <FeatureItem key={f.name} icon={f.icon} name={f.name} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* DIVIDER */}
        <div className="landing-divider" aria-hidden="true" />
        <div className="landing-divider-right" aria-hidden="true" />

        {/* ===== RIGHT PANEL ===== */}
        <section className="panel-right" style={{ color: "black" }} aria-label="Get started with Kleido AI">
          <div className="panel-right-content" style={{ marginLeft: 100 }}>
            {/* EYEBROW */}
            <div className="eyebrow" aria-hidden="true" style={{ color: "white" }}>
              <span className="eyebrow-dot" />
              Now in early access
            </div>

            {/* HEADLINE */}
            <h2 className="right-headline" style={{ color: "black" }}>
              Experience the<br />
              <span
                className="accent-line-right"
                style={{
                  background: "linear-gradient(90deg, #000000 0%, #666666 65%, #ffffff 105%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                new kind of
              </span>
              <br />
              Social Ecosystem
              <ValuePeek />
            </h2>

            {/* CTA CARDS */}
            <div className="cta-stack">
              {/* Join — gated behind REACT_APP_JOIN_ENABLED */}
              <button
                type="button"
                onClick={handleJoin}
                disabled={joinDisabled}
                aria-disabled={joinDisabled}
                aria-describedby={flagError ? "join-flag-error" : undefined}
                className={`cta-card cta-secondary${joinDisabled ? " is-disabled" : ""}`}
                title={joinDisabled ? NOT_LIVE_MESSAGE : undefined}
                aria-label={
                  joinDisabled
                    ? "Join Kleido AI — not available yet"
                    : "Join Kleido AI and create your account"
                }
                style={{
                  background: "linear-gradient(180deg, rgba(111, 108, 108, 0.92), rgba(220,220,220,0.75))",
                  border: "1px solid rgba(240, 238, 238, 0.08)",
                  color: "#111",
                  boxShadow: "0 12px 35px rgba(232, 221, 221, 0.12)",
                }}
              >
                <div className="cta-text-block">
                  <span className="cta-title" style={{ color: "#111", fontWeight: 700 }}>
                    Join Kleido AI
                    {joinDisabled && <span className="cta-lock">Coming soon</span>}
                  </span>

                  <span className="cta-subtitle" style={{ color: "#444" }}>
                    {joinDisabled
                      ? "Sign-ups open once the app goes live"
                      : "Create/Signin your account today"}
                  </span>
                </div>

                <div className="cta-arrow" aria-hidden="true" style={{ color: "#111" }}>
                  {joinDisabled ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </button>

              {/* Error surfaced when a gated CTA is clicked */}
              {flagError && (
                <div id="join-flag-error" className="flag-error" role="alert" aria-live="assertive">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{flagError}</span>
                </div>
              )}

              {/* Experience / demo */}
              <button
                type="button"
                onClick={handleExperience}
                className="cta-card cta-secondary"
                aria-label="Experience Kleido AI demo platform"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(220,220,220,0.75))",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  color: "#111",
                  boxShadow: "0 12px 35px rgba(0,0,0,0.12)",
                }}
              >
                <div className="cta-text-block">
                  <span className="cta-title" style={{ color: "#111", fontWeight: 700 }}>
                    Experience Kleido AI
                  </span>

                  <span className="cta-subtitle" style={{ color: "#444" }}>
                    Explore the demo platform
                  </span>
                </div>

                <div className="cta-arrow" aria-hidden="true" style={{ color: "#111" }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>

            <div style={{ height: 100, width: "100%", marginTop: 40, borderRadius: 20 }}>
              <StoreLinks />
            </div>
          </div>

          <div
            className="mt-20 text-xs text-muted-2"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ color: "black", fontSize: 20, textAlign: "center" }}>
              Kleido AI, An Initiumnet company.
            </div>
          </div>
        </section>
      </main>
    </>
  );
}